import moment from 'moment';
import Change from '../models/Change';

const DEADLINE_SOON_DAYS = 7;

export function isDeadlineOverdue(change: Change, now = moment()): boolean {
    return Boolean(change.deadline?.isBefore(now, 'day'));
}

export function isDeadlineSoon(change: Change, now = moment()): boolean {
    if (!change.deadline || isDeadlineOverdue(change, now)) {
        return false;
    }
    return !change.deadline.isAfter(now.clone().add(DEADLINE_SOON_DAYS, 'days'), 'day');
}

export function compareByPriority(a: Change, b: Change): number {
    const priorityDiff = (b.priority ?? 0) - (a.priority ?? 0);
    if (priorityDiff !== 0) {
        return priorityDiff;
    }
    const aUpdated = a.updatedAt ? moment(a.updatedAt).valueOf() : 0;
    const bUpdated = b.updatedAt ? moment(b.updatedAt).valueOf() : 0;
    return bUpdated - aUpdated;
}

export function compareByCreatedAt(
    a: Change,
    b: Change,
    direction: 'newest' | 'oldest'
): number {
    const aCreated = a.createdAt ? moment(a.createdAt).valueOf() : 0;
    const bCreated = b.createdAt ? moment(b.createdAt).valueOf() : 0;
    const diff = aCreated - bCreated;
    return direction === 'newest' ? -diff : diff;
}

/**
 * Sort by last update descending.
 * Uses change.updatedAt (any card change, including the current user's).
 */
export function compareByUpdatedAt(a: Change, b: Change): number {
    const aUpdated = a.updatedAt ? moment(a.updatedAt).valueOf() : 0;
    const bUpdated = b.updatedAt ? moment(b.updatedAt).valueOf() : 0;
    return bUpdated - aUpdated;
}

/**
 * Smart sort: overdue → deadline soon → rest.
 * Within a bucket: deadline asc (when relevant), then priority desc, then updatedAt desc.
 */
export function compareTodoChanges(a: Change, b: Change, now = moment()): number {
    const bucket = (change: Change) => {
        if (isDeadlineOverdue(change, now)) {
            return 0;
        }
        if (isDeadlineSoon(change, now)) {
            return 1;
        }
        return 2;
    };

    const bucketDiff = bucket(a) - bucket(b);
    if (bucketDiff !== 0) {
        return bucketDiff;
    }

    if (bucket(a) < 2) {
        const deadlineDiff = (a.deadline?.valueOf() ?? 0) - (b.deadline?.valueOf() ?? 0);
        if (deadlineDiff !== 0) {
            return deadlineDiff;
        }
    }

    return compareByPriority(a, b);
}

export function compareOverdueChanges(a: Change, b: Change, now = moment()): number {
    if (isDeadlineOverdue(a, now) !== isDeadlineOverdue(b, now)) {
        return isDeadlineOverdue(a, now) ? -1 : 1;
    }
    if (a.deadline && b.deadline) {
        const deadlineDiff = a.deadline.valueOf() - b.deadline.valueOf();
        if (deadlineDiff !== 0) {
            return deadlineDiff;
        }
    }
    return compareByPriority(a, b);
}

export function compareAllChanges(a: Change, b: Change): number {
    if (a.statusIndex() === b.statusIndex()) {
        return compareByPriority(a, b);
    }
    return a.statusIndex() - b.statusIndex();
}
